import React, { useState } from 'react';
import { Box, Typography, Button, TextField, Avatar, Dialog, DialogContent, IconButton } from '@mui/material';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import PersonIcon from '@mui/icons-material/Person';

export interface ApplicantData {
    currentStatus?: string;
    referrals: {
        status: string;
        codeUsed: string;
        referredBy: string;
        date: string;
    };
    basicProfile: {
        fullName: string;
        mobileNo: string;
        email: string;
        dob: string;
        gender: string;
        languagesRead: string[];
        languagesWrite: string[];
    };
    address: {
        addressLine1: string;
        addressLine2: string;
        country: string;
        state: string;
        district: string;
        pincode: string;
    };
    selfieUrl: string;
    contact: {
        primaryMobile: string;
        primaryEmail: string;
        altMobile: string;
        altEmail: string;
    };
    education: {
        highestQualification: string;
        schoolName: string;
        boardName: string;
        yearOfCompletion: string;
        documents: string[];
    };
    twoWheeler: {
        vehicleRegNo: string;
        rcBookFileName: string;
        rcBookFileUrl: string;
    };
    experience: {
        hasExperience: string;
        organizationName: string;
        yearsOfExperience: string;
    };
    documents: {
        panCard: string;
        panFileName: string;
        panFileUrl: string;
        drivingLicense: string;
        expiryDate: string;
        frontSideFileName: string;
        frontSideFileUrl: string;
        backSideFileName: string;
        backSideFileUrl: string;
    };
}

interface ApplicantDetailProps {
    applicant: ApplicantData;
    onBack: () => void;
    onApprove: (reason: string) => void;
    onReject: (reason: string) => void;
    onHold: (reason: string) => void;
}

const TABS = [
    'Referrals',
    'Basic Profile',
    'Address Details',
    'Selfie Upload',
    'Contact',
    'Education',
    'Two Wheeler Info',
    'Experience',
    'Documents',
];

const handlePreviewFile = (fileUrl?: string) => {
    if (!fileUrl) return;
    window.open(fileUrl, '_blank');
};

const handleDownloadFile = async (fileUrl?: string, fileName?: string) => {
    if (!fileUrl) return;
    try {
        const response = await fetch(fileUrl);
        if (!response.ok) throw new Error(`Failed to fetch file: ${response.status}`);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName || 'download';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
        console.error('Download failed, falling back to opening the file instead:', error);
        window.open(fileUrl, '_blank');
    }
};

const ReadField = ({
    label,
    value,
    withIcons = false,
    fileUrl,
    style,
    valueStyle,
}: {
    label: string;
    value: string;
    withIcons?: boolean;
    fileUrl?: string;
    style?: React.CSSProperties;
    valueStyle?: React.CSSProperties;
}) => (
    <div className="col-auto" style={{ minWidth: 220 }}>
        <Typography sx={{ fontSize: 13, color: '#191a1b', mb: '6px', ...style }}>{label}</Typography>
        <TextField
            value={value || ''}
            size="small"
            variant="outlined"
            InputProps={{
                readOnly: true,
                endAdornment: withIcons ? (
                    <Box display="flex" gap={0.5}>
                        <IconButton size="small" onClick={() => handleDownloadFile(fileUrl, value)} disabled={!fileUrl}>
                            <DownloadIcon sx={{ fontSize: 18, color: fileUrl ? '#5a5d63' : '#c9cbd1' }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => handlePreviewFile(fileUrl)} disabled={!fileUrl}>
                            <VisibilityIcon sx={{ fontSize: 18, color: fileUrl ? '#5a5d63' : '#c9cbd1' }} />
                        </IconButton>
                    </Box>
                ) : undefined,
            }}
            sx={{
                backgroundColor: '#fff',
                minWidth: 220,
                '& .MuiOutlinedInput-input': { fontSize: 14, color: '#1A3353', ...valueStyle },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
            }}
        />
    </div>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#09090a', mb: 3 }}>{children}</Typography>
);

function ApplicantDetail({ applicant, onBack, onApprove, onReject, onHold }: ApplicantDetailProps) {
    const [activeTab, setActiveTab] = useState(0);
    const [modal, setModal] = useState<null | 'reject' | 'onhold' | 'Approve'>(null);
    const [reason, setReason] = useState('');
    const primaryColor = '#1A3353';
    const isLastTab = activeTab === TABS.length - 1;

    const canChangeStatus =
        !applicant.currentStatus ||
        applicant.currentStatus === 'New' ||
        applicant.currentStatus === 'Submitted' ||
        applicant.currentStatus === 'Hold';

    const goNext = () => setActiveTab((t) => Math.min(t + 1, TABS.length - 1));
    const goBack = () => (activeTab === 0 ? onBack() : setActiveTab((t) => t - 1));

    const closeModal = () => {
        setModal(null);
        setReason('');
    };

    const submitModal = () => {
        if (modal === 'reject') onReject(reason);
        if (modal === 'onhold') onHold(reason);
        if (modal === 'Approve') onApprove(reason);
        closeModal();
    };

    const renderReferrals = () => (
        <div>
            <SectionTitle><b>Referrals</b></SectionTitle>
            <div className="d-flex flex-column" style={{ maxWidth: 420, rowGap: 14 }}>
                <div className="d-flex align-items-center">
                    <Typography sx={{ fontSize: 13, color: '#191a1b', width: 110, flexShrink: 0 }}>
                        Status
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#191a1b' }}>
                        {applicant.referrals?.status}
                    </Typography>
                </div>
                <div className="d-flex align-items-center">
                    <Typography sx={{ fontSize: 13, color: '#191a1b', width: 110, flexShrink: 0 }}>
                        Code Used
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#191a1b' }}>
                        {applicant.referrals?.codeUsed}
                    </Typography>
                </div>
                <div className="d-flex align-items-center">
                    <Typography sx={{ fontSize: 13, color: '#191a1b', width: 110, flexShrink: 0 }}>
                        Referred by
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#191a1b' }}>
                        {applicant.referrals?.referredBy}
                    </Typography>
                </div>
                <div className="d-flex align-items-center">
                    <Typography sx={{ fontSize: 13, color: '#191a1b', width: 110, flexShrink: 0 }}>
                        Date
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#191a1b' }}>
                        {applicant.referrals?.date}
                    </Typography>
                </div>
            </div>
        </div>
    );

    const renderBasicProfile = () => (
        <div>
            <SectionTitle>Basic Details</SectionTitle>
            <div className="row gx-4 gy-4">
                <ReadField label="Full Name" value={applicant.basicProfile?.fullName} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
                <ReadField label="Mobile No." value={applicant.basicProfile?.mobileNo} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
                <ReadField label="Email address" value={applicant.basicProfile?.email} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
            </div>
            <div className="row gx-4 gy-4 mt-1">
                <ReadField label="Date of Birth" value={applicant.basicProfile?.dob} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
                <ReadField label="Gender" value={applicant.basicProfile?.gender} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
            </div>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#191a1b", mt: 4, mb: 2 }}>
                Languages Known
            </Typography>
            <div className="row gx-5">
                <div className="col-auto">
                    <Typography sx={{ fontSize: 13, color: '#191a1b', mb: '6px' }}>Read</Typography>
                    {(applicant.basicProfile?.languagesRead || []).map((lang) => (
                        <Typography key={lang} sx={{ fontSize: 14, color: "#191a1b", mb: '4px' }}>
                            {lang}
                        </Typography>
                    ))}
                </div>
                <div className="col-auto">
                    <Typography sx={{ fontSize: 13, color: '#191a1b', mb: '6px' }}>Write</Typography>
                    {(applicant.basicProfile?.languagesWrite || []).map((lang) => (
                        <Typography key={lang} sx={{ fontSize: 14, color: "#191a1b", mb: '4px' }}>
                            {lang}
                        </Typography>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderAddress = () => (
        <div>
            <SectionTitle>Current Address</SectionTitle>
            <div className="row gx-4 gy-4">
                <div className="col-6">
                    <Typography sx={{ fontSize: 13, color: '#191a1b', mb: '6px' }}>Address Line 1</Typography>
                    <TextField
                        value={applicant.address?.addressLine1 || ''}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: true }}
                        sx={{ backgroundColor: '#fff' }}
                    />
                </div>
                <div className="col-6">
                    <Typography sx={{ fontSize: 13, color: '#191a1b', mb: '6px' }}>Address Line 2</Typography>
                    <TextField
                        value={applicant.address?.addressLine2 || ''}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: true }}
                        sx={{ backgroundColor: '#fff' }}
                    />
                </div>
            </div>
            <div className="row gx-4 gy-4 mt-1">
                <ReadField label="Country" value={applicant.address?.country} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
                <ReadField label="State" value={applicant.address?.state} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
                <ReadField label="District" value={applicant.address?.district} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
                <ReadField label="Pincode" value={applicant.address?.pincode} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
            </div>
        </div>
    );

    const renderSelfie = () => (
        <div>
            {applicant.selfieUrl ? (
                <Avatar
                    src={applicant.selfieUrl}
                    sx={{ width: 110, height: 110, border: '3px solid #fff', boxShadow: '0 0 0 1px #e0e0e0' }}
                />
            ) : (
                <Avatar sx={{ width: 110, height: 110, bgcolor: '#e0e0e0', boxShadow: '0 0 0 1px #e0e0e0' }}>
                    <PersonIcon sx={{ fontSize: 50, color: '#9a9da3' }} />
                </Avatar>
            )}
        </div>
    );

    const renderContact = () => (
        <div className="row gx-4 gy-4">
            <ReadField label="Primary Mobile Number" value={applicant.contact?.primaryMobile} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
            <ReadField label="Primary Email Address" value={applicant.contact?.primaryEmail} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
            <ReadField label="Alternative Mobile Number" value={applicant.contact?.altMobile} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
            <ReadField label="Alternative Email Address" value={applicant.contact?.altEmail} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
        </div>
    );

    const renderEducation = () => (
        <div>
            <div className="row gx-4 gy-4">
                <ReadField label="Highest Qualification" value={applicant.education?.highestQualification} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
                <ReadField label="School Name" value={applicant.education?.schoolName} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
                <ReadField label="Board Name" value={applicant.education?.boardName} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
                <ReadField label="Year of Completion" value={applicant.education?.yearOfCompletion} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
            </div>
            {(applicant.education?.documents || []).length > 0 && (
                <>
                    <Typography sx={{ fontSize: 13, color: '#6c6f75', mt: 4, mb: 2 }}>Document</Typography>
                    <div className="d-flex gap-2">
                        {(applicant.education?.documents || []).map((doc, i) => (
                            <Box
                                key={doc + i}
                                onClick={() => handlePreviewFile(doc)}
                                sx={{
                                    width: 60,
                                    height: 60,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    backgroundColor: '#fff',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    '&:hover .doc-overlay': { opacity: 1 },
                                }}
                            >
                                <Box
                                    component="img"
                                    src={doc}
                                    alt={`document-${i}`}
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                <Box
                                    className="doc-overlay"
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        backgroundColor: 'rgba(0,0,0,0.35)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: 0,
                                        transition: 'opacity 0.15s ease-in-out',
                                    }}
                                >
                                    <VisibilityIcon sx={{ color: '#fff', fontSize: 20 }} />
                                </Box>
                            </Box>
                        ))}
                    </div>
                </>
            )}
        </div>
    );

    const renderTwoWheeler = () => (
        <div className="row gx-4 gy-4">
            <ReadField label="Vehicle Registered Number" value={applicant.twoWheeler?.vehicleRegNo} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
            <ReadField
                label="Photo of RC Book"
                value={applicant.twoWheeler?.rcBookFileName}
                withIcons
                fileUrl={applicant.twoWheeler?.rcBookFileUrl}
                style={{ color: '#191a1b' }}
                valueStyle={{ color: '#191a1b' }}
            />
        </div>
    );

    const renderExperience = () => (
        <div className="row gx-4 gy-4">
            <ReadField label="Previous Experience In Field Verification" value={applicant.experience?.hasExperience} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
            <ReadField label="Organization Name" value={applicant.experience?.organizationName} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
            <ReadField label="Years of Experience" value={applicant.experience?.yearsOfExperience} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
        </div>
    );

    const renderDocuments = () => (
        <div>
            <div className="row gx-4 gy-4">
                <ReadField label="PAN Card" value={applicant.documents?.panCard} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
                <ReadField
                    label="Photo or Upload"
                    value={applicant.documents?.panFileName}
                    withIcons
                    fileUrl={applicant.documents?.panFileUrl}
                    style={{ color: '#191a1b' }}
                    valueStyle={{ color: '#191a1b' }}
                />
            </div>
            <div className="row gx-4 gy-4 mt-1">
                <ReadField label="Driving License" value={applicant.documents?.drivingLicense} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
                <ReadField label="Expiry Date" value={applicant.documents?.expiryDate} style={{ color: '#191a1b' }} valueStyle={{ color: '#191a1b' }} />
                <ReadField
                    label="Front Side Image"
                    value={applicant.documents?.frontSideFileName}
                    withIcons
                    fileUrl={applicant.documents?.frontSideFileUrl}
                    style={{ color: '#191a1b' }}
                    valueStyle={{ color: '#191a1b' }}
                />
                <ReadField
                    label="Back Side Image"
                    value={applicant.documents?.backSideFileName}
                    withIcons
                    fileUrl={applicant.documents?.backSideFileUrl}
                    style={{ color: '#191a1b' }}
                    valueStyle={{ color: '#191a1b' }}
                />
            </div>
        </div>
    );

    const TAB_CONTENT = [
        renderReferrals,
        renderBasicProfile,
        renderAddress,
        renderSelfie,
        renderContact,
        renderEducation,
        renderTwoWheeler,
        renderExperience,
        renderDocuments,
    ];

    return (
        <div className="p-3" style={{ backgroundColor: "white" }}>
            <div className="d-flex align-items-center mb-3" style={{ cursor: 'pointer' }} onClick={onBack}>
                <ArrowBackIcon sx={{ fontSize: 16, color: primaryColor, mr: 1 }} />
                <Typography sx={{ fontWeight: 600, color: primaryColor, fontSize: 14 }}>Back</Typography>
            </div>

            <Box
                display="flex"
                gap={4}
                sx={{
                    borderBottom: '1px solid #e0e0e0',
                    width: '100%',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
                {TABS.map((tab, index) => (
                    <Typography
                        key={tab}
                        onClick={() => setActiveTab(index)}
                        sx={{
                            cursor: 'pointer',
                            paddingBottom: '10px',
                            whiteSpace: 'nowrap',
                            position: 'relative',
                            fontSize: 15,
                            color: activeTab === index ? '#000' : '#9a9da3',
                            fontWeight: activeTab === index ? 600 : 500,
                            '&::after':
                                activeTab === index
                                    ? {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: '-1px',
                                        left: 0,
                                        width: '100%',
                                        height: '2px',
                                        backgroundColor: '#ff3b30',
                                    }
                                    : {},
                        }}
                    >
                        {tab}
                    </Typography>
                ))}
            </Box>

            <div className="content-area py-4" style={{ minHeight: 320 }}>
                {TAB_CONTENT[activeTab]()}
            </div>

            <div className="d-flex justify-content-end align-items-center gap-2 pt-3" style={{ borderTop: '1px solid #e0e0e0' }}>
                <Button
                    variant="outlined"
                    onClick={goBack}
                    sx={{ textTransform: 'none', borderColor: '#c9cbd1', color: primaryColor, minWidth: 90 }}
                >
                    Back
                </Button>

                {!isLastTab && (
                    <Button
                        variant="contained"
                        onClick={goNext}
                        sx={{ textTransform: 'none', backgroundColor: '#ff3b30', minWidth: 90, boxShadow: 'none' }}
                    >
                        Next
                    </Button>
                )}

                {isLastTab && canChangeStatus && (
                    <>
                        <Button
                            variant="outlined"
                            onClick={() => setModal('reject')}
                            sx={{ textTransform: 'none', borderColor: '#ff3b30', color: '#ff3b30', minWidth: 90 }}
                        >
                            Reject
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setModal('onhold')}
                            sx={{ textTransform: 'none', backgroundColor: '#e0a800', minWidth: 90, boxShadow: 'none' }}
                        >
                            On-hold
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setModal('Approve')}
                            sx={{ textTransform: 'none', backgroundColor: '#2e7d32', minWidth: 90, boxShadow: 'none' }}
                        >
                            Approve
                        </Button>
                    </>
                )}
            </div>

            <Dialog open={modal !== null} onClose={closeModal} maxWidth="xs" fullWidth>
                <DialogContent sx={{ p: 3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: primaryColor, mb: 2 }}>
                        Reason for {modal === 'reject' ? 'Rejection' : modal === 'onhold' ? 'On-hold' : 'Approval'}
                    </Typography>
                    <TextField
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        multiline
                        minRows={5}
                        fullWidth
                        placeholder="Type reason here"
                        sx={{ backgroundColor: '#f5f5f5' }}
                    />
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button
                            variant="outlined"
                            onClick={closeModal}
                            sx={{ textTransform: 'none', borderColor: '#c9cbd1', color: primaryColor, minWidth: 90 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={submitModal}
                            sx={{ textTransform: 'none', backgroundColor: '#ff3b30', minWidth: 90, boxShadow: 'none' }}
                        >
                            Submit
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default ApplicantDetail;
